import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { History, Loader2 } from 'lucide-react';
import { getTaskSignatures } from '../../services/api';
import type { TaskSignatureResponse } from '../../services/api/types.gen';
import { SignatureCard } from './SignatureCard';

interface SignatureHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
}

export const SignatureHistoryDialog: React.FC<SignatureHistoryDialogProps> = ({
  open,
  onOpenChange,
  taskId,
}) => {
  const [signatures, setSignatures] = useState<TaskSignatureResponse[] | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getTaskSignatures({
      path: { task_id: taskId },
      query: { include_invalidated: true },
    })
      .then(res => {
        if (cancelled) return;
        setSignatures(res.data ?? []);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load signature history:', err);
        setError('Unable to load signature history. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, taskId]);

  const sorted = (signatures ?? [])
    .slice()
    .sort(
      (a, b) =>
        new Date(b.signed_at).getTime() - new Date(a.signed_at).getTime()
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[560px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <History className='h-5 w-5 text-primary' />
            Signature history
          </DialogTitle>
          <DialogDescription>
            All electronic signatures and invalidations for this chart, newest
            first.
          </DialogDescription>
        </DialogHeader>
        <div className='max-h-[60vh] overflow-y-auto pr-1'>
          {loading ? (
            <div className='flex items-center justify-center py-8 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin mr-2' />
              Loading...
            </div>
          ) : error ? (
            <div className='py-6 text-sm text-destructive'>{error}</div>
          ) : sorted.length === 0 ? (
            <div className='py-6 text-sm text-muted-foreground'>
              No signatures have been recorded for this chart yet.
            </div>
          ) : (
            <div className='space-y-2'>
              {sorted.map(sig => (
                <SignatureCard key={sig.id} signature={sig} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
